import type { VirtualFile } from "@pdfking/pdfengine"
import type { Expand } from "@pdfking/tfunc"
import type { FileStorageBackend } from "../files/types/FileStorageBackend"
import { Init } from "@pdfking/reactivity/preact"
import {
  contains,
  delete_duplicates,
  filter,
  length,
  to_array,
} from "@pdfking/rfunc"
import {
  Constant,
  CreateSingleton,
  KeyDrop,
  Keys,
  RecordMap,
  SortBy,
  Void,
} from "@pdfking/tfunc"
import CreateStorage from "../files/providers/create"
import { DefaultBackend } from "../files/storage/DefaultBackend"
import persistence from "../utils/persistence"

const store = persistence("storage")

type StorageProviderMethods = Omit<FileStorageBackend, "files" | "populate">

type StorageForward = Expand<
  { files: () => Record<string, VirtualFile> } & {
    [K in keyof StorageProviderMethods]: () => StorageProviderMethods[K]
  }
>

const method_defaults: Record<string, any> = {
  files: Constant({}),
  add_files: Void,
  remove_files: Void,
  open_files: Void,
  open_folders: Void,
  refresh: Void,
  cleanup: Void,
}

const provider_forward: StorageForward = RecordMap(
  method_defaults,
  (_v, k) => k,
  (fallback, k) =>
    function (this: any) {
      const method = this.provider?.[k]
      return method?.bind?.(this.provider) ?? method ?? fallback
    }
) as any

export default CreateSingleton(() =>
  Init({
    computed: {
      all_files_checked: {
        get() {
          const s = length(this.selected_files)
          const t = length(this.files)
          return s == t && t > 0
        },
        set(v: boolean) {
          this.unselected_files_names = v ? [] : Keys(this.files)
        },
      },
      ...provider_forward,
      selected_files(): Record<string, VirtualFile> {
        return KeyDrop(this.files, this.unselected_files_names)
      },
      selected_files_names: {
        get() {
          return SortBy(Keys(this.selected_files))
        },
        set(v: string[]) {
          this.unselected_files_names = Keys(KeyDrop(this.files, v))
        },
      },
    },
    data: {
      config: {} as any,
      last_session: null as string | null,
      provider: null as FileStorageBackend | null,
      unselected_files_names: [] as string[],
    },
    methods: {
      select_files(paths: string[]) {
        this.selected_files_names = delete_duplicates(
          to_array(this.selected_files_names, paths)
        )
        return paths
      },
      set_config(spec: any, no_store = false) {
        const config = spec || DefaultBackend
        CreateStorage(config).then((provider: FileStorageBackend) => {
          this.config = config
          this.provider = provider
          if (!no_store) store.set(config)
          provider.populate()
        })
      },
      unselect_files(paths: string[]) {
        this.selected_files_names = filter(
          (k: string) => !contains(paths, k),
          this.selected_files_names
        )
        return paths
      },
    },
    init() {
      store.get().then((config: any) => this.set_config(config, true))
    },
  })
)
